import {
  WSElement,
  WSText,
  WSTimeline,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import {
  Invoice,
  InvoicePaymentEvents,
  InvoicePaymentStatusReason
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import capitalize from "lodash/capitalize";
import { TextBulleted } from "../../../shared/components/TextBulleted";

type Props = {
  invoice: Invoice;
};

const statusReasonMap: Record<InvoicePaymentStatusReason, string> = {
  [InvoicePaymentStatusReason.INSUFFICIENT_FUNDS]: "Insufficient funds",
  [InvoicePaymentStatusReason.ACCOUNT_NOT_FOUND]: "Account not found",
  [InvoicePaymentStatusReason.UNAUTHORIZED]: "Unauthorized",
  [InvoicePaymentStatusReason.GENERIC_BANK_ERROR]: "Bank error",
  [InvoicePaymentStatusReason.GENERIC_CARD_ERROR]: "Card error",
  [InvoicePaymentStatusReason.CARD_CURRENCY_NOT_SUPPORTED]:
    "Card currency not supported",
  [InvoicePaymentStatusReason.CARD_EXPIRED]: "Card expired",
  [InvoicePaymentStatusReason.CARD_INPUT_INVALID]: "Invalid card input"
};

const getEventDetails = (event: keyof InvoicePaymentEvents, date: Date) => {
  switch (event) {
    case "initiatedAt":
      return `Payment initiated (${toWSDateString(date, "monthDayYear")})`;
    case "completedAt":
      return `Payment completed (${toWSDateString(date, "monthDayYear")})`;
    case "failedAt":
      return `Payment failed (${toWSDateString(date, "monthDayYear")})`;
    case "returnedAt":
      return `Payment returned (${toWSDateString(date, "monthDayYear")})`;
    case "cancelledAt":
      return `Payment cancelled (${toWSDateString(date, "monthDayYear")})`;
  }
};

export const InvoicePayments: React.FC<Props> = ({ invoice }) => {
  return (
    <WSTimeline
      items={(invoice.payments || []).map(payment => {
        const events = Object.entries(payment.events || {})
          .filter(([, date]) => !!date)
          .map(([key, date]) => ({
            event: key as keyof InvoicePaymentEvents,
            date: date as Date
          }));

        events.sort((a, b) => a.date.getTime() - b.date.getTime());

        return {
          date: events[0]?.date,
          pills: [
            payment.status,
            `${formatMoney(payment.amount ?? 0, invoice.currency || "USD")} payment`
          ].filter(Boolean) as string[],
          content: (
            <>
              {payment.sourceMetadata?.institution && (
                <WSElement mb="M">
                  <WSText.ParagraphSm color="gray400">
                    Payment method:{" "}
                  </WSText.ParagraphSm>
                  <WSText color="gray600">
                    {capitalize(payment.sourceMetadata.institution)}
                    {payment.sourceMetadata.mask
                      ? ` (${payment.sourceMetadata.mask})`
                      : ""}
                  </WSText>
                </WSElement>
              )}

              <TextBulleted color="gray600">
                {events.map(event => (
                  <li key={event.event}>
                    <WSText.ParagraphSm>
                      {getEventDetails(event.event, event.date)}
                    </WSText.ParagraphSm>
                    {(event.event === "failedAt" ||
                      event.event === "returnedAt") &&
                      payment.statusReason && (
                        <WSText.ParagraphSm color="gray600">
                          {statusReasonMap[payment.statusReason]}
                        </WSText.ParagraphSm>
                      )}
                  </li>
                ))}
              </TextBulleted>
            </>
          )
        };
      })}
    />
  );
};
